<template>
  <div class="splash-screen">
    <div class="splash-container">
      <!-- Logo -->
      <div class="logo-container">
        <img src="@/assets/logo-v2.png" alt="Dietedics" class="logo" />
      </div>

      <!-- Under Construction Message -->
      <div class="message-container">
        <h1 class="main-title">We're Under Construction</h1>
        <p class="subtitle">
          We're working hard to build the best platform for dietitians to discover and explore continuing education opportunities.
        </p>
      </div>

      <!-- Email Submission Form -->
      <div class="form-section">
        <h2 class="form-title">Stay Updated</h2>
        <p class="form-description">Enter your email address to receive updates about our launch:</p>
        <form @submit.prevent="handleEmailSubmit" class="email-form">
          <div class="input-group">
            <input
              v-model="email"
              type="email"
              placeholder="your.email@example.com"
              required
              class="input-field"
              :disabled="emailSubmitting"
            />
            <button
              type="submit"
              class="submit-button"
              :disabled="emailSubmitting || !email"
            >
              <span v-if="emailSubmitting">Submitting...</span>
              <span v-else>Submit</span>
            </button>
          </div>
          <p v-if="emailSuccess" class="success-message">
            {{ emailSuccessMessage }}
          </p>
          <p v-if="emailError" class="error-message">
            {{ emailError }}
          </p>
        </form>
      </div>

      <!-- Early Access Code Form -->
      <div class="form-section">
        <h2 class="form-title">Early Access</h2>
        <p class="form-description">Have an early access code? Enter it below to access the platform:</p>
        <form @submit.prevent="handleAccessCodeSubmit" class="access-code-form">
          <div class="input-group">
            <input
              v-model="accessCode"
              type="text"
              placeholder="Enter your early access code"
              required
              class="input-field"
              :disabled="codeValidating"
              @input="accessCode = $event.target.value"
            />
            <button
              type="submit"
              class="submit-button"
              :disabled="codeValidating || !accessCode"
            >
              <span v-if="codeValidating">Validating...</span>
              <span v-else>Access Platform</span>
            </button>
          </div>
          <p v-if="codeError" class="error-message">
            {{ codeError }}
          </p>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { submitEmail, validateEarlyAccessCode, saveEarlyAccessCode } from '@/services/splashService.js'
import { handleApiError } from '@/services/errorHandler.js'

export default {
  name: 'SplashScreen',
  data() {
    return {
      email: '',
      accessCode: '',
      emailSubmitting: false,
      codeValidating: false,
      emailSuccess: false,
      emailSuccessMessage: '',
      emailError: '',
      codeError: ''
    }
  },
  methods: {
    async handleEmailSubmit() {
      this.emailError = ''
      this.emailSuccess = false
      this.emailSubmitting = true

      try {
        await submitEmail(this.email)
        this.emailSuccess = true
        this.emailSuccessMessage = 'Thank you for your interest! We\'ll be in touch soon.'
        this.email = ''
      } catch (error) {
        // Error is already handled by errorHandler, but we can show a specific message
        const errorInfo = handleApiError(error, { silent: true })
        if (errorInfo.messages && errorInfo.messages.length > 0) {
          this.emailError = errorInfo.messages[0]
        } else {
          this.emailError = 'Failed to submit email. Please try again.'
        }
      } finally {
        this.emailSubmitting = false
      }
    },
    async handleAccessCodeSubmit() {
      this.codeError = ''
      this.codeValidating = true

      try {
        const response = await validateEarlyAccessCode(this.accessCode)
        
        if (response.valid) {
          // Save code to localStorage
          saveEarlyAccessCode(this.accessCode.toUpperCase())
          // Emit event to parent to hide splash screen
          this.$emit('access-granted')
        } else {
          this.codeError = response.message || 'Invalid early access code'
        }
      } catch (error) {
        // Check if error response contains the validation result (404 with JSON body)
        if (error.status === 404 && error.data && typeof error.data === 'object' && error.data.valid === false) {
          // Invalid code - just show the message, don't show error modal
          this.codeError = error.data.message || 'Invalid early access code'
        } else if (error.status === 400 && error.data && typeof error.data === 'object' && error.data.valid === false) {
          // Bad request (missing code) - just show the message
          this.codeError = error.data.message || 'Early access code is required'
        } else {
          // Server error (500+) or network error - show error modal
          handleApiError(error)
          this.codeError = 'Failed to validate code. Please try again.'
        }
      } finally {
        this.codeValidating = false
      }
    }
  }
}
</script>

<style scoped>
.splash-screen {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-background-main);
  padding: 20px;
}

.splash-container {
  max-width: 600px;
  width: 100%;
  background-color: var(--color-surface-light);
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.logo-container {
  text-align: center;
  margin-bottom: 40px;
}

.logo {
  max-width: 250px;
  height: auto;
}

.message-container {
  text-align: center;
  margin-bottom: 40px;
}

.main-title {
  font-size: 2rem;
  color: var(--color-text-primary);
  margin: 0 0 15px 0;
  font-weight: 600;
}

.subtitle {
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin: 0;
}

.form-section {
  margin-bottom: 40px;
}

.form-section:last-child {
  margin-bottom: 0;
}

.form-title {
  font-size: 1.5rem;
  color: var(--color-text-primary);
  margin: 0 0 10px 0;
  font-weight: 600;
}

.form-description {
  font-size: 1rem;
  color: var(--color-text-secondary);
  margin: 0 0 20px 0;
  line-height: 1.5;
}

.input-group {
  display: flex;
  gap: 10px;
  flex-direction: column;
}

.input-field {
  width: 100%;
  padding: 12px 16px;
  font-size: 1rem;
  border: 2px solid var(--color-border-light);
  border-radius: 6px;
  background-color: var(--color-surface-light);
  color: var(--color-text-primary);
  transition: border-color var(--transition-normal);
  box-sizing: border-box;
}

.input-field:focus {
  outline: none;
  border-color: var(--color-primary-main);
}

.input-field:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.submit-button {
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 500;
  color: var(--color-text-inverse);
  background-color: var(--color-primary-main);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color var(--transition-normal);
  white-space: nowrap;
}

.submit-button:hover:not(:disabled) {
  background-color: var(--color-primary-dark);
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.success-message {
  margin: 15px 0 0 0;
  padding: 12px;
  background-color: var(--color-success-light);
  color: var(--color-success-dark);
  border-radius: 6px;
  font-size: 0.95rem;
  text-align: center;
}

.error-message {
  margin: 15px 0 0 0;
  padding: 12px;
  background-color: var(--color-error-light);
  color: var(--color-error-dark);
  border-radius: 6px;
  font-size: 0.95rem;
  text-align: center;
}

@media (min-width: 768px) {
  .input-group {
    flex-direction: row;
  }

  .input-field {
    flex: 1;
  }

  .submit-button {
    min-width: 140px;
  }
}

@media (max-width: 480px) {
  .splash-container {
    padding: 30px 20px;
  }

  .main-title {
    font-size: 1.5rem;
  }

  .subtitle {
    font-size: 1rem;
  }

  .form-title {
    font-size: 1.25rem;
  }
}
</style>
